import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import _ from 'lodash'
import { useIntl } from 'react-intl'
import { Box, Button, CircularProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import FinishExamDialog from '../../../components/dialogs/FinishExamDialog'
import storage from '../../../utils/storageService'
import { isErrorWithMessage } from '../../../utils/hepers'
import { testApiSlice } from '../../../features/section/testSlice'
import { Answer, StudentAnswer } from './components/types'
import ExamFinishedDialog from './components/ExamFinishedDialog'
import ExamFinishSuccessDialog from './components/ExamFinishSuccessDialog'
import Question from './components/Question'
import ExamNavigation from './components/ExamNavigation'
import ExamTimer from './components/ExamTimer'

const styles = {
    wrapper: {
        height: '100vh',
        background: '#fff',
        overflow: 'hidden',
    },
    container: {
        maxWidth: '1200px',
        margin: 'auto',
        width: '100%',
        height: '95%',

        // overflow: 'auto',
        background: '#ffffff',
        padding: '20px',
        maxHeight: '100vh',

        '@media screen and (max-width: 500px)': {
            width: 'auto',
            padding: '10px',
            overflow: 'hidden',
        },
    },
    examWrapper: {
        display: 'flex',
        position: 'relative',
        justifyContent: 'space-between',
    },
    main: {
        width: '100%',
        minHeight: '100%',
        height: 'calc(100vh - 120px)',
        maxWidth: '900px',
        pb: 10,
        mr: 2,
        flexGrow: 2,
        overflow: 'auto',

        '@media screen and (max-width: 500px)': {
            height: `calc(100vh - 270px)`,
        },
    },
    aside: {
        width: '100%',
        maxWidth: '270px',

        '@media screen and (max-width: 500px)': {
            maxWidth: '100%',
        },
    },
    title: {
        mb: 4,

        '@media screen and (max-width: 500px)': {
            mb: 1,
        },
    },
}

const ExamPage = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { examId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')

    const [open, setOpen] = useState(false)
    const [openSuccess, setOpenSuccess] = useState(false)

    const [studentAnswers, setStudentAnswers] = useState<StudentAnswer[]>(
        storage.get(`exam_${examId}`) || [],
    )

    const [finishExamResponse, setFinishExamResponse] = useState<any>({})
    // cacheBusting to disable rtkq cache
    const timestampRef = useRef(Date.now()).current
    const {
        data: exam,
        isFetching: isFetchingExam,
        error,
    } = testApiSlice.useFetchAllExamQuestionsQuery({
        topicTestId: examId,
        userTopicEnrollId: searchParams.get('user'),
        topicTestResultId: searchParams.get('topicTestResultId'),
        sessionId: timestampRef,
    })

    const [finishExam, { isLoading: isFinishing }] =
        testApiSlice.useFinishExamMutation()

    const startedAt = exam ? new Date(exam?.startedAt) : null

    const handleSelect = (
        answer: Answer,
        {
            questionId,
            questionType,
        }: { questionId: number; questionType: string },
    ) => {
        const newStudentAnswers = _.cloneDeep(studentAnswers)

        const answeredQuestionIndex = _.findIndex(
            studentAnswers,
            (item) => item.questionId === questionId,
        )

        if (newStudentAnswers[answeredQuestionIndex]) {
            const answeredOption = _.find(
                newStudentAnswers[answeredQuestionIndex].answers,
                (item) => item.id === answer.id,
            )

            if (answeredOption) {
                const newAnsweredOptions = _.filter(
                    newStudentAnswers[answeredQuestionIndex].answers,
                    (item) => item.id !== answeredOption.id,
                )

                newStudentAnswers[answeredQuestionIndex].answers =
                    newAnsweredOptions

                if (!newAnsweredOptions.length) {
                    newStudentAnswers.splice(answeredQuestionIndex, 1)
                }

                setStudentAnswers(newStudentAnswers)

                return
            }

            if (questionType === 'ONE_ANSWER') {
                newStudentAnswers[answeredQuestionIndex].answers = []
            }

            newStudentAnswers[answeredQuestionIndex].answers.push({
                answerId: answer.id,
                isEmpty: false,
            })

            setStudentAnswers(newStudentAnswers)

            return
        }

        const newAnswer = {
            questionId,
            questionType,
            answers: [{ answerId: answer.id, isEmpty: false }],
        }

        newStudentAnswers.push(newAnswer)

        setStudentAnswers(newStudentAnswers)
    }

    const onFinishExam = async () => {
        try {
            const response = await finishExam({
                answers: studentAnswers,
                topicTestResultId: exam.topicTestResultId,
            }).unwrap()
            setOpen(false)
            setOpenSuccess(true)
            setFinishExamResponse(response)
            storage?.remove(`exam_${examId}`)
        } catch (error) {
            console.log('error', error)
        }
    }

    useEffect(() => {
        storage.set(`exam_${examId}`, studentAnswers)
    }, [studentAnswers])

    useEffect(() => {
        if (exam) {
            setSearchParams({
                topicTestId: examId,
                user: searchParams.get('user'),
                topicTestResultId: `${exam.topicTestResultId}`,
            })
        }
    }, [exam])

    if (isErrorWithMessage(error) && error?.data?.errors[0].code === 1022) {
        return (
            <ExamFinishedDialog
                open={!!error}
                confirmAction={() => navigate(-1)}
            />
        )
    }

    return (
        <Box sx={styles.wrapper}>
            {isFetchingExam ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : exam ? (
                <Box sx={styles.container}>
                    <Box sx={styles.title}>
                        <H3>{exam?.topicTestTitle}</H3>
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                            mb: 4,
                            pb: 4,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',

                            '@media screen and (max-width: 500px)': {
                                flexWrap: 'wrap',
                                flexDirection: 'column-reverse',
                                mb: 0,
                                pb: 0,
                            },
                        }}
                    >
                        <Box sx={styles.main}>
                            {exam?.questions?.map((question, index) => (
                                <Question
                                    key={question.id}
                                    index={index}
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    handleSelect={handleSelect}
                                />
                            ))}

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={() => setOpen(true)}
                                sx={{
                                    '@media screen and (min-width: 501px)': {
                                        display: 'none',
                                    },
                                }}
                            >
                                {formatMessage({ id: 'finish_the_exam' })}
                            </Button>
                        </Box>

                        <Box sx={styles.aside}>
                            <ExamTimer
                                onFinishExam={() => onFinishExam()}
                                expiryTimestamp={
                                    new Date(
                                        startedAt.getTime() +
                                            exam.duration * 1000 * 60,
                                    )
                                }
                            />

                            <ExamNavigation
                                questions={exam?.questions}
                                studentAnswers={studentAnswers}
                            />

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={() => setOpen(true)}
                                sx={{
                                    '@media screen and (max-width: 500px)': {
                                        display: 'none',
                                    },
                                }}
                            >
                                {formatMessage({ id: 'finish_the_exam' })}
                            </Button>
                        </Box>
                    </Box>

                    <FinishExamDialog
                        open={open}
                        questions={exam?.questions}
                        studentAnswers={studentAnswers}
                        isFinishing={isFinishing}
                        confirmAction={() => onFinishExam()}
                        handleClose={() => setOpen(false)}
                    />

                    <ExamFinishSuccessDialog
                        open={openSuccess}
                        finishExamResponse={finishExamResponse}
                        confirmAction={() =>
                            navigate(`/my-courses/exam-result/${examId}`)
                        }
                        handleClose={() => setOpenSuccess(false)}
                    />
                </Box>
            ) : (
                <Box sx={styles.wrapper}>No data</Box>
            )}
        </Box>
    )
}

export default ExamPage
